import {
  formatBatchPeriod,
  formatRecipientTypeLabel,
  isPendingDistributionBatch,
} from './revenueSharing.utils';

export const sumRevenueAmounts = (items) => {
  if (!items?.length) return '0';
  let total = 0;
  for (const item of items) {
    const n = Number(item.amount);
    if (!Number.isFinite(n)) continue;
    total += n;
  }
  return String(total);
};

export const computeTransparencyStats = (revenueItems, batches, microjobSummary = null) => {
  const confirmedOrDistributed = (revenueItems || []).filter(
    (i) => i.status === 'confirmed' || i.status === 'distributed',
  );
  const totalRevenueAllTime = sumRevenueAmounts(confirmedOrDistributed);

  const distributedItems = (revenueItems || []).filter((i) => i.status === 'distributed');
  const totalDistributedRevenue = sumRevenueAmounts(distributedItems);

  const batchList = batches || [];
  const vaultPendingCycles = batchList.filter((b) => isPendingDistributionBatch(b.status)).length;
  const vaultExecutedPayouts = batchList.filter((b) => b.status === 'executed').length;

  const vaultPayoutVolume = batchList
    .filter((b) => b.status === 'executed')
    .reduce((s, b) => s + (Number(b.totalRevenue) || 0), 0);

  const microjob = microjobSummary || {};
  const microjobExecutedCycles = Number(microjob.executedCycleCount) || 0;
  const microjobPendingCycles = Number(microjob.pendingCycleCount) || 0;
  const microjobPayoutVolume = Number(microjob.totalPayoutVolume) || 0;
  const microjobContributorCount = Number(microjob.contributorCount) || 0;

  return {
    totalRevenueAllTime,
    totalDistributedRevenue,
    pendingCycles: vaultPendingCycles + microjobPendingCycles,
    executedPayouts: vaultExecutedPayouts + microjobExecutedCycles,
    totalPayoutVolume: String(vaultPayoutVolume + microjobPayoutVolume),
    revenueItemCount: confirmedOrDistributed.length,
    vaultPendingCycles,
    vaultExecutedPayouts,
    vaultPayoutVolume: String(vaultPayoutVolume),
    microjobExecutedCycles,
    microjobPendingCycles,
    microjobPayoutVolume: String(microjobPayoutVolume),
    microjobContributorCount,
  };
};

/** Sum allocations per wallet from executed distribution batches. */
export const aggregateRecipientAllocations = (batches, options = {}) => {
  const { recipientFilter = '', recipientType = '' } = options;
  if (recipientType === 'microjob') return [];
  const needle = recipientFilter.trim().toLowerCase();
  const byAddr = new Map();

  for (const batch of batches || []) {
    if (batch.status !== 'executed') continue;
    for (const d of batch.distributions || []) {
      const addr = (d.recipientAddress || '').toLowerCase();
      if (!addr) continue;
      if (needle && !addr.includes(needle)) continue;
      if (recipientType && d.recipientType !== recipientType) continue;

      let row = byAddr.get(addr);
      if (!row) {
        row = {
          recipientAddress: d.recipientAddress,
          recipientTypes: new Set(),
          allocatedTotal: 0,
          allocationCount: 0,
        };
        byAddr.set(addr, row);
      }
      row.recipientTypes.add(d.recipientType);
      const n = Number(d.amount);
      if (Number.isFinite(n)) row.allocatedTotal += n;
      row.allocationCount += 1;
    }
  }

  return Array.from(byAddr.values()).map(formatAllocationRow);
};

const formatAllocationRow = (row) => ({
  recipientAddress: row.recipientAddress,
  recipientTypes: Array.from(row.recipientTypes),
  recipientTypeLabels: Array.from(row.recipientTypes)
    .map((t) => formatRecipientTypeLabel(t))
    .join(', '),
  allocatedTotal: String(row.allocatedTotal),
  allocationCount: row.allocationCount,
});

/** Sum allocations per wallet from executed microjob cycles. */
export const aggregateMicrojobRecipientAllocations = (microjobPayouts, options = {}) => {
  const { recipientFilter = '', recipientType = '' } = options;
  if (recipientType && recipientType !== 'microjob') return [];

  const needle = recipientFilter.trim().toLowerCase();
  const byAddr = new Map();

  for (const payout of microjobPayouts || []) {
    const addr = (payout.contributorWallet || '').toLowerCase();
    if (!addr) continue;
    if (needle && !addr.includes(needle)) continue;

    let row = byAddr.get(addr);
    if (!row) {
      row = {
        recipientAddress: payout.contributorWallet,
        recipientTypes: new Set(['microjob']),
        allocatedTotal: 0,
        allocationCount: 0,
      };
      byAddr.set(addr, row);
    }
    const n = Number(payout.amount);
    if (Number.isFinite(n)) row.allocatedTotal += n;
    row.allocationCount += 1;
  }

  return Array.from(byAddr.values()).map(formatAllocationRow);
};

export const mergeRecipientAllocationRows = (batchAllocations = [], microjobAllocations = []) => {
  const byAddr = new Map();

  const addRow = (row, sourceKey) => {
    const addr = (row.recipientAddress || '').toLowerCase();
    if (!addr) return;
    const current = byAddr.get(addr);
    const amt = Number(row.allocatedTotal) || 0;
    if (!current) {
      byAddr.set(addr, {
        recipientAddress: row.recipientAddress,
        recipientTypes: new Set(row.recipientTypes || []),
        allocatedTotal: amt,
        vaultAllocatedTotal: sourceKey === 'vaultAllocatedTotal' ? amt : 0,
        microjobAllocatedTotal: sourceKey === 'microjobAllocatedTotal' ? amt : 0,
        allocationCount: row.allocationCount || 0,
      });
      return;
    }
    for (const type of row.recipientTypes || []) current.recipientTypes.add(type);
    current.allocatedTotal += amt;
    current[sourceKey] += amt;
    current.allocationCount += row.allocationCount || 0;
  };

  for (const row of batchAllocations) addRow(row, 'vaultAllocatedTotal');
  for (const row of microjobAllocations) addRow(row, 'microjobAllocatedTotal');

  return Array.from(byAddr.values())
    .map((row) => ({
      ...formatAllocationRow(row),
      vaultAllocatedTotal: String(row.vaultAllocatedTotal),
      microjobAllocatedTotal: String(row.microjobAllocatedTotal),
    }))
    .sort((a, b) => Number(b.allocatedTotal) - Number(a.allocatedTotal));
};

/** Merge allocation totals, claim DB summary, and optional on-chain claimable. */
export const mergeRecipientPayoutSummaries = (allocations, claimSummaries = [], claimableByAddress = {}) => {
  const claimMap = {};
  for (const c of claimSummaries || []) {
    const addr = (c.recipientAddress || '').toLowerCase();
    if (addr) claimMap[addr] = c;
  }

  const allocMap = {};
  for (const a of allocations || []) {
    const addr = (a.recipientAddress || '').toLowerCase();
    if (addr) allocMap[addr] = a;
  }

  const addrSet = new Set([...Object.keys(allocMap), ...Object.keys(claimMap)]);

  const rows = [];
  for (const addr of addrSet) {
    if (!addr) continue;
    const alloc = allocMap[addr];
    const claim = claimMap[addr];
    const allocated = Number(alloc?.allocatedTotal ?? 0);
    const claimed = Number(claim?.totalClaimed ?? 0);
    const vaultAllocated = Number(alloc?.vaultAllocatedTotal ?? 0);
    const microjobAllocated = Number(alloc?.microjobAllocatedTotal ?? 0);
    const vaultClaimed = Number(claim?.vaultClaimed ?? 0);
    const microjobClaimed = Number(claim?.microjobClaimed ?? 0);
    const claimableRaw = claimableByAddress[addr];
    const claimableNum =
      claimableRaw != null && claimableRaw !== '' && Number.isFinite(Number(claimableRaw))
        ? Number(claimableRaw)
        : null;

    rows.push({
      id: addr,
      recipientAddress: alloc?.recipientAddress || claim?.recipientAddress || addr,
      recipientTypeLabels: alloc?.recipientTypeLabels || '—',
      allocatedTotal: alloc?.allocatedTotal ?? '0',
      vaultAllocatedTotal: alloc?.vaultAllocatedTotal ?? '0',
      microjobAllocatedTotal: alloc?.microjobAllocatedTotal ?? '0',
      allocationCount: alloc?.allocationCount ?? 0,
      totalClaimed: claim?.totalClaimed ?? '0',
      vaultClaimed: claim?.vaultClaimed ?? '0',
      microjobClaimed: claim?.microjobClaimed ?? '0',
      claimCount: claim?.claimCount ?? 0,
      vaultClaimCount: claim?.vaultClaimCount ?? 0,
      microjobClaimCount: claim?.microjobClaimCount ?? 0,
      lastClaimedAt: claim?.lastClaimedAt ?? null,
      vaultLastClaimedAt: claim?.vaultLastClaimedAt ?? null,
      microjobLastClaimedAt: claim?.microjobLastClaimedAt ?? null,
      claimableOnChain: claimableNum != null ? String(claimableNum) : null,
      vaultPending:
        Number.isFinite(vaultAllocated) && Number.isFinite(vaultClaimed)
          ? String(Math.max(0, vaultAllocated - vaultClaimed))
          : null,
      microjobPending:
        Number.isFinite(microjobAllocated) && Number.isFinite(microjobClaimed)
          ? String(Math.max(0, microjobAllocated - microjobClaimed))
          : null,
      pendingEstimate:
        Number.isFinite(allocated) && Number.isFinite(claimed)
          ? String(Math.max(0, allocated - claimed))
          : null,
    });
  }

  return rows.sort((a, b) => Number(b.allocatedTotal) - Number(a.allocatedTotal));
};

/** Flatten executed batch distributions for payout ledger line items. */
export const flattenPayoutLedgerRows = (batches, recipientFilter = '', recipientType = '') => {
  const needle = recipientFilter.trim().toLowerCase();
  const rows = [];

  for (const batch of batches || []) {
    if (batch.status !== 'executed') continue;
    const cycle = formatBatchPeriod(batch.periodStart, batch.periodEnd);
    const distributions = batch.distributions || [];
    for (const d of distributions) {
      const addr = (d.recipientAddress || '').toLowerCase();
      if (needle && !addr.includes(needle)) continue;
      if (recipientType && d.recipientType !== recipientType) continue;
      rows.push({
        id: `${batch._id}-${addr}-${d.recipientType}-${d.amount}`,
        batchId: batch._id,
        cycle,
        recipientAddress: d.recipientAddress,
        recipientType: d.recipientType,
        recipientTypeLabel: formatRecipientTypeLabel(d.recipientType),
        amount: d.amount,
        txHash: batch.txHash,
        splitVersion: batch.splitVersion,
        executedAt: batch.executedAt,
      });
    }
  }

  return rows;
};

/** Flatten executed microjob payouts for payout ledger line items. */
export const flattenMicrojobPayoutLedgerRows = (
  microjobPayouts,
  recipientFilter = '',
  recipientType = '',
) => {
  if (recipientType && recipientType !== 'microjob') return [];

  const needle = recipientFilter.trim().toLowerCase();
  const rows = [];

  for (const payout of microjobPayouts || []) {
    const addr = (payout.contributorWallet || '').toLowerCase();
    if (needle && !addr.includes(needle)) continue;
    rows.push({
      id: `microjob-${payout.cycleId}-${addr}-${payout.amount}`,
      batchId: payout.cycleId,
      cycle: formatBatchPeriod(payout.periodStart, payout.periodEnd),
      recipientAddress: payout.contributorWallet,
      recipientType: 'microjob',
      recipientTypeLabel: formatRecipientTypeLabel('microjob'),
      amount: payout.amount,
      txHash: payout.onChainTxHash,
      splitVersion: null,
      executedAt: payout.executedAt,
    });
  }

  return rows;
};

export const flattenCombinedPayoutLedgerRows = (
  batches,
  microjobPayouts,
  recipientFilter = '',
  recipientType = '',
) => {
  const rows = [
    ...flattenPayoutLedgerRows(batches, recipientFilter, recipientType),
    ...flattenMicrojobPayoutLedgerRows(microjobPayouts, recipientFilter, recipientType),
  ];

  return rows.sort((a, b) => {
    const ta = a.executedAt ? new Date(a.executedAt).getTime() : 0;
    const tb = b.executedAt ? new Date(b.executedAt).getTime() : 0;
    return tb - ta;
  });
};
